import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IoAddCircle, IoCloseCircle } from 'react-icons/io5';

const MENU_WIDTH = 260;
const VIEWPORT_MARGIN = 12;

/**
 * A filter dropdown anchored under its tab, in the shape Claritask uses: a
 * header, then one toggle row per option. A row already in the filter shows an
 * ✕ (click to drop it); one that is not shows a ⊕ (click to add it). Every
 * toggle applies immediately.
 *
 * Portaled to <body> on purpose. The palette's backdrop uses backdrop-filter,
 * which makes it a containing block for fixed-position descendants, so a menu
 * rendered inside it would be positioned against the backdrop and clipped by
 * the results panel's overflow.
 */
const FacetMenu = ({ anchor, groups, onClose }) => {
    const menuRef = useRef(null);

    // Dismiss on a click anywhere else, or on Escape.
    useEffect(() => {
        const onMouseDown = (event) => {
            if (menuRef.current?.contains(event.target)) return;
            // The caret that opened the menu handles its own toggle.
            if (event.target?.closest?.('.ck-gs-tabcaret, .ck-gs-tab')) return;
            onClose();
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [onClose]);

    if (!anchor) {
        return null;
    }

    // Keep the menu on screen when its tab sits near the right edge.
    const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(anchor.left, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN)
    );

    return createPortal(
        <div
            ref={menuRef}
            className="ck-gs-facet"
            style={{ top: anchor.top, left, width: MENU_WIDTH }}
            role="group"
        >
            {groups.map((group) => (
                <div key={group.key} className="ck-gs-facetgroup">
                    <div className="ck-gs-facethead">{group.title}</div>

                    {group.options.length === 0 && (
                        <div className="ck-gs-facetempty">{group.emptyLabel ?? 'Nothing to filter by'}</div>
                    )}

                    {group.options.map((option) => (
                        <a
                            key={option.key ?? option.value}
                            className={`ck-gs-facetrow ${option.active ? '' : 'is-off'}`}
                            title={option.active ? 'Click to remove this filter' : 'Click to filter by this'}
                            onClick={() => group.onToggle(option.value)}
                        >
                            {option.active ? <IoCloseCircle /> : <IoAddCircle />}
                            {option.color && (
                                <span className="ck-gs-facetbullet" style={{ backgroundColor: option.color }} />
                            )}
                            <span className="ck-gs-facetlabel">{option.label}</span>
                            {option.hint && <span className="ck-gs-facethint">{option.hint}</span>}
                        </a>
                    ))}
                </div>
            ))}
        </div>,
        document.body
    );
};

export default FacetMenu;
